/**
 * =============================================================================
 * GLOBAL COMMAND BAR - Universal Platform Navigation & Command System
 * =============================================================================
 * 
 * PHASE 2A: Platform Interaction Foundation
 * 
 * This is NOT just search - this is the operational command center:
 * - Universal navigation (should replace sidebar usage for power users)
 * - Quick actions (New Job, Assign Contractor, Upload Evidence, etc.)
 * - Entity search (Jobs, Contractors, Parks, Plots, Assets, Compliance, Invoices, PPM)
 * - Keyboard-first UX (Cmd+K, Arrow navigation, Enter to select, Esc to close)
 * 
 * Goal: Users should almost stop using the sidebar
 */

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { 
  Search, Command, ArrowRight, Briefcase, Building2, Users, 
  DollarSign, Database, FileText, Clock, AlertTriangle,
  Home, LayoutDashboard, TrendingUp, Shield, X, Plus,
  UserPlus, Camera, Upload, Receipt, Calendar, MapPin,
  Wrench, Settings, HelpCircle, Zap, CheckCircle,
  ArrowUp, ArrowDown, CornerDownLeft, ChevronRight
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

// =============================================================================
// QUICK ACTIONS - Commands that execute actions or navigate
// =============================================================================
const QUICK_ACTIONS = [
  // Navigation - Core Workspaces
  { id: 'dashboard', label: 'Operations Dashboard', description: 'View command centre', path: '/phs/helpdesk', icon: LayoutDashboard, keywords: ['dashboard', 'home', 'main', 'command', 'centre'], category: 'navigate' },
  { id: 'pipeline', label: 'Pipeline Board', description: 'Kanban job view', path: '/phs/helpdesk/board', icon: TrendingUp, keywords: ['pipeline', 'board', 'kanban'], category: 'navigate' },
  { id: 'assets', label: 'Asset Workspace', description: 'Parks, plots, compliance', path: '/phs/assets', icon: Database, keywords: ['assets', 'workspace'], category: 'navigate' },
  { id: 'contractors', label: 'Contractor Workspace', description: 'Manage contractors', path: '/phs/contractors', icon: Users, keywords: ['contractors', 'trades', 'workspace'], category: 'navigate' },
  { id: 'finance', label: 'Finance Workspace', description: 'Invoices & payments', path: '/phs/finance', icon: DollarSign, keywords: ['finance', 'invoices', 'payments', 'workspace'], category: 'navigate' },
  
  // Quick Actions - Create / Execute
  { id: 'new-job', label: 'New Job', description: 'Log a new job', path: '/phs/helpdesk?action=new-job', icon: Plus, keywords: ['new', 'create', 'job', 'log', 'add'], category: 'action', highlight: true },
  { id: 'assign-contractor', label: 'Assign Contractor', description: 'Assign to open job', path: '/phs/helpdesk?action=assign', icon: UserPlus, keywords: ['assign', 'contractor', 'allocate'], category: 'action' },
  { id: 'upload-evidence', label: 'Upload Evidence', description: 'Add photos to job', path: '/phs/helpdesk?action=evidence', icon: Camera, keywords: ['upload', 'evidence', 'photos', 'images'], category: 'action' },
  { id: 'create-followon', label: 'Create Follow-On', description: 'Create follow-on job', path: '/phs/helpdesk?action=follow-on', icon: ArrowRight, keywords: ['follow', 'on', 'create', 'remedial'], category: 'action' },
  { id: 'add-invoice', label: 'Add Invoice', description: 'Submit new invoice', path: '/phs/finance?action=add-invoice', icon: Receipt, keywords: ['add', 'invoice', 'submit'], category: 'action' },
  { id: 'add-ppm', label: 'Add PPM Task', description: 'Schedule PPM task', path: '/phs/assets?tab=ppm&action=add', icon: Calendar, keywords: ['add', 'ppm', 'task', 'schedule'], category: 'action' },
  { id: 'create-quote', label: 'Create Quote', description: 'Generate new quote', path: '/phs/helpdesk?action=quote', icon: FileText, keywords: ['create', 'quote', 'generate'], category: 'action' },
  
  // Navigation - Asset Tabs
  { id: 'parks', label: 'Parks', description: 'View all parks', path: '/phs/assets?tab=parks', icon: Building2, keywords: ['parks', 'sites', 'locations'], category: 'navigate' },
  { id: 'plots', label: 'Plots', description: 'View all plots', path: '/phs/assets?tab=plots', icon: Home, keywords: ['plots', 'units', 'homes'], category: 'navigate' },
  { id: 'compliance', label: 'Compliance', description: 'Certificates & expiries', path: '/phs/assets?tab=compliance', icon: Shield, keywords: ['compliance', 'certificates', 'expiry'], category: 'navigate' },
  { id: 'ppm-schedule', label: 'PPM Schedule', description: 'Planned maintenance', path: '/phs/assets?tab=ppm', icon: Calendar, keywords: ['ppm', 'schedule', 'maintenance', 'planned'], category: 'navigate' },
  
  // Navigation - Contractor Tabs
  { id: 'contractor-performance', label: 'Contractor Performance', description: 'SLA & metrics', path: '/phs/contractors?tab=performance', icon: TrendingUp, keywords: ['performance', 'sla', 'metrics', 'contractor'], category: 'navigate' },
  { id: 'contractor-compliance', label: 'Contractor Compliance', description: 'Accreditations', path: '/phs/contractors?tab=compliance', icon: Shield, keywords: ['contractor', 'compliance', 'accreditation'], category: 'navigate' },
  
  // Navigation - Finance Views
  { id: 'awaiting-invoice', label: 'Awaiting Invoice', description: 'Jobs needing invoices', path: '/phs/finance?view=awaiting-invoice', icon: Clock, keywords: ['invoice', 'awaiting', 'pending'], category: 'navigate' },
  { id: 'invoice-review', label: 'Invoice Review', description: 'Invoices to review', path: '/phs/finance?view=invoice-review', icon: FileText, keywords: ['invoice', 'review', 'check'], category: 'navigate' },
  { id: 'payments', label: 'Payments', description: 'Payment processing', path: '/phs/finance?view=payments', icon: DollarSign, keywords: ['payments', 'pay', 'process'], category: 'navigate' },
  
  // Navigation - Job Queues
  { id: 'overdue', label: 'Overdue Jobs', description: 'SLA breached jobs', path: '/phs/helpdesk?filter=overdue', icon: AlertTriangle, keywords: ['overdue', 'late', 'breach', 'sla'], category: 'navigate' },
  { id: 'awaiting-quote', label: 'Awaiting Quote', description: 'Jobs needing quotes', path: '/phs/helpdesk?status=Awaiting%20Quote', icon: FileText, keywords: ['awaiting', 'quote', 'pending'], category: 'navigate' },
  { id: 'quote-review', label: 'Quote Review', description: 'Quotes to approve', path: '/phs/helpdesk?status=Quote%20Awaiting%20Review', icon: FileText, keywords: ['quote', 'review', 'approve'], category: 'navigate' },
  { id: 'scheduled', label: 'Scheduled Jobs', description: 'Upcoming visits', path: '/phs/helpdesk?status=Scheduled', icon: Calendar, keywords: ['scheduled', 'upcoming', 'visits'], category: 'navigate' },
  { id: 'in-progress', label: 'In Progress', description: 'Active jobs', path: '/phs/helpdesk?status=In%20Progress', icon: Wrench, keywords: ['progress', 'active', 'working'], category: 'navigate' },
  
  // Governance & Support
  { id: 'user-guide', label: 'User Guide', description: 'Help documentation', path: '/phs/governance/user-guide', icon: HelpCircle, keywords: ['help', 'guide', 'documentation', 'user'], category: 'navigate' },
  { id: 'sops', label: 'SOPs', description: 'Standard procedures', path: '/phs/governance/sops', icon: FileText, keywords: ['sop', 'procedures', 'standards'], category: 'navigate' },
];

// Category labels and ordering
const CATEGORY_ORDER = ['action', 'navigate'];
const CATEGORY_LABELS = {
  action: 'Quick Actions',
  navigate: 'Navigation',
  jobs: 'Jobs',
  contractors: 'Contractors',
  parks: 'Parks',
  invoices: 'Invoices'
};

export default function GlobalCommandBar({ isOpen, onClose }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState({ actions: QUICK_ACTIONS, jobs: [], contractors: [], parks: [], invoices: [] });
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [loading, setLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState(null); // null = show all
  const inputRef = useRef(null);
  const resultsRef = useRef(null);
  const navigate = useNavigate();

  const token = localStorage.getItem('wyldecrest_token') || localStorage.getItem('token');
  const headers = useMemo(() => ({ Authorization: `Bearer ${token}` }), [token]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
      setQuery('');
      setSelectedIndex(0);
      setActiveCategory(null);
      setResults({ actions: QUICK_ACTIONS, jobs: [], contractors: [], parks: [], invoices: [] });
    }
  }, [isOpen]);

  // Build flat list of all results for keyboard navigation
  const flatResults = useMemo(() => {
    const items = [];
    
    // Add actions first
    results.actions.forEach(action => {
      items.push({ type: 'action', data: action, category: action.category });
    });
    
    // Add entity results
    results.jobs.forEach(job => {
      items.push({ type: 'job', data: job, category: 'jobs' });
    });
    results.contractors.forEach(contractor => {
      items.push({ type: 'contractor', data: contractor, category: 'contractors' });
    });
    results.parks.forEach(park => {
      items.push({ type: 'park', data: park, category: 'parks' });
    });
    results.invoices.forEach(invoice => {
      items.push({ type: 'invoice', data: invoice, category: 'invoices' });
    });
    
    return items;
  }, [results]);

  // Search function
  const performSearch = useCallback(async (searchQuery) => {
    if (!searchQuery.trim()) {
      setResults({ actions: QUICK_ACTIONS, jobs: [], contractors: [], parks: [], invoices: [] });
      return;
    }

    const q = searchQuery.toLowerCase();
    
    // Filter quick actions immediately (synchronous)
    const filteredActions = QUICK_ACTIONS.filter(action => 
      action.label.toLowerCase().includes(q) ||
      action.description?.toLowerCase().includes(q) ||
      action.keywords.some(k => k.includes(q))
    );

    // Immediately update with filtered actions (before API calls)
    setResults(prev => ({ ...prev, actions: filteredActions }));
    
    setLoading(true);
    try {
      // Search jobs
      const jobsRes = await axios.get(`${API}/api/wyldecrest/jobs`, {
        params: { search: searchQuery, limit: 6 },
        headers
      });
      const jobs = (jobsRes.data?.jobs || []).slice(0, 6);

      // Search contractors
      const contractorsRes = await axios.get(`${API}/api/contractors`, { headers });
      const allContractors = contractorsRes.data?.contractors || contractorsRes.data || [];
      const contractors = allContractors
        .filter(c => 
          c.name?.toLowerCase().includes(q) || 
          c.company_name?.toLowerCase().includes(q) ||
          c.primary_trade?.toLowerCase().includes(q)
        )
        .slice(0, 4);

      // Search parks
      const parksRes = await axios.get(`${API}/api/wyldecrest/parks`, { headers });
      const parks = (parksRes.data?.parks || [])
        .filter(p => 
          p.name?.toLowerCase().includes(q) || 
          p.postcode?.toLowerCase().includes(q) ||
          p.address?.toLowerCase().includes(q)
        )
        .slice(0, 4);

      // Search invoices (if query looks like invoice-related)
      let invoices = [];
      if (['inv', 'invoice', 'payment', 'pay'].some(term => q.includes(term))) {
        try {
          const invoicesRes = await axios.get(`${API}/api/invoices`, { 
            params: { search: searchQuery, limit: 4 },
            headers 
          });
          invoices = (invoicesRes.data?.invoices || invoicesRes.data || []).slice(0, 4);
        } catch (err) {
          console.error('Invoice search error:', err);
        }
      }

      setResults({ actions: filteredActions, jobs, contractors, parks, invoices });
    } catch (err) {
      console.error('Search error:', err);
      setResults({ actions: filteredActions, jobs: [], contractors: [], parks: [], invoices: [] });
    } finally {
      setLoading(false);
    }
  }, [headers]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      performSearch(query);
    }, 150);
    return () => clearTimeout(timer);
  }, [query, performSearch]);

  // Handle selection
  const handleSelect = useCallback((index) => {
    const item = flatResults[index];
    if (!item) return;

    switch (item.type) {
      case 'action':
        navigate(item.data.path);
        break;
      case 'job':
        navigate(`/phs/helpdesk/job/${item.data.id}`);
        break;
      case 'contractor':
        navigate(`/phs/contractors?selected=${item.data.id}`);
        break;
      case 'park':
        navigate(`/phs/assets?tab=parks&selected=${item.data.id || item.data.park_id}`);
        break;
      case 'invoice':
        navigate(`/phs/finance?view=invoice-review&selected=${item.data.id}`);
        break;
      default:
        break;
    }
    onClose();
  }, [flatResults, navigate, onClose]);

  // Keyboard navigation
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e) => {
      const totalItems = flatResults.length;
      
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setSelectedIndex(prev => Math.min(prev + 1, totalItems - 1));
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setSelectedIndex(prev => Math.max(prev - 1, 0));
      } else if (e.key === 'Enter') {
        e.preventDefault();
        handleSelect(selectedIndex);
      } else if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, selectedIndex, flatResults, handleSelect, onClose]);

  // Scroll selected item into view
  useEffect(() => {
    if (resultsRef.current) {
      const selectedEl = resultsRef.current.querySelector(`[data-index="${selectedIndex}"]`);
      if (selectedEl) {
        selectedEl.scrollIntoView({ block: 'nearest', behavior: 'smooth' });
      }
    }
  }, [selectedIndex]);

  // Group actions by category for display - must be before conditional return
  const groupedActions = useMemo(() => {
    const groups = { action: [], navigate: [] };
    results.actions.forEach(action => {
      const cat = action.category || 'navigate';
      if (groups[cat]) groups[cat].push(action);
    });
    return groups;
  }, [results.actions]);

  if (!isOpen) return null;

  let globalIndex = 0;

  return (
    <div className="fixed inset-0 z-[100]" data-testid="global-command-bar">
      {/* Backdrop */}
      <div 
        className="fixed inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Command Panel */}
      <div className="fixed top-[12%] left-1/2 -translate-x-1/2 w-full max-w-2xl px-4">
        <div className="bg-white rounded-2xl shadow-2xl border border-slate-200 overflow-hidden">
          {/* Search Input */}
          <div className="flex items-center gap-3 p-4 border-b border-slate-100">
            <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-teal-50">
              <Command className="w-4 h-4 text-teal-600" />
            </div>
            <input
              ref={inputRef}
              type="text"
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setSelectedIndex(0);
              }}
              placeholder="Search or type a command..."
              className="flex-1 text-base outline-none placeholder:text-slate-400"
              data-testid="command-search-input"
            />
            {loading && (
              <div className="animate-spin w-4 h-4 border-2 border-teal-500 border-t-transparent rounded-full" />
            )}
            <div className="flex items-center gap-1 text-xs text-slate-400">
              <kbd className="px-1.5 py-0.5 rounded bg-slate-100 font-mono">esc</kbd>
            </div>
          </div>

          {/* Results */}
          <div ref={resultsRef} className="max-h-[60vh] overflow-y-auto">
            {/* Quick Actions */}
            {groupedActions.action.length > 0 && (
              <div className="p-2 border-b border-slate-100">
                <p className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Quick Actions</p>
                <div className="space-y-0.5">
                  {groupedActions.action.map((action) => {
                    const Icon = action.icon;
                    const isSelected = selectedIndex === globalIndex;
                    const currentIndex = globalIndex++;
                    return (
                      <button
                        key={action.id}
                        data-index={currentIndex}
                        onClick={() => handleSelect(currentIndex)}
                        className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                          isSelected ? 'bg-teal-50 ring-2 ring-teal-500/20' : 'hover:bg-slate-50'
                        }`}
                      >
                        <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isSelected ? 'bg-teal-100' : 'bg-slate-100'}`}>
                          <Icon className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-slate-500'}`} />
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className={`text-sm font-medium ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>{action.label}</p>
                          <p className="text-xs text-slate-400 truncate">{action.description}</p>
                        </div>
                        {action.highlight && (
                          <span className="px-2 py-0.5 text-[10px] font-semibold rounded-full bg-teal-100 text-teal-700">NEW</span>
                        )}
                        <ChevronRight className={`w-4 h-4 ${isSelected ? 'text-teal-500' : 'text-slate-300'}`} />
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Navigation */}
            {groupedActions.navigate.length > 0 && (
              <div className="p-2 border-b border-slate-100">
                <p className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Navigation</p>
                <div className="grid grid-cols-2 gap-1">
                  {groupedActions.navigate.map((action) => {
                    const Icon = action.icon;
                    const isSelected = selectedIndex === globalIndex;
                    const currentIndex = globalIndex++;
                    return (
                      <button
                        key={action.id}
                        data-index={currentIndex}
                        onClick={() => handleSelect(currentIndex)}
                        className={`flex items-center gap-2 px-3 py-2 rounded-lg text-left transition ${
                          isSelected ? 'bg-teal-50 ring-2 ring-teal-500/20' : 'hover:bg-slate-50'
                        }`}
                      >
                        <Icon className={`w-4 h-4 flex-shrink-0 ${isSelected ? 'text-teal-600' : 'text-slate-400'}`} />
                        <span className={`text-sm truncate ${isSelected ? 'text-teal-700 font-medium' : 'text-slate-600'}`}>{action.label}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Jobs */}
            {results.jobs.length > 0 && (
              <div className="p-2 border-b border-slate-100">
                <p className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Jobs</p>
                {results.jobs.map((job) => {
                  const isSelected = selectedIndex === globalIndex;
                  const currentIndex = globalIndex++;
                  return (
                    <button
                      key={job.id}
                      data-index={currentIndex}
                      onClick={() => handleSelect(currentIndex)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                        isSelected ? 'bg-teal-50 ring-2 ring-teal-500/20' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isSelected ? 'bg-teal-100' : 'bg-blue-50'}`}>
                        <Briefcase className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-blue-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>
                          {job.job_reference || `JOB-${job.id?.slice(0,6)}`}
                        </p>
                        <p className="text-xs text-slate-400 truncate">{job.description || job.category}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        job.status === 'Completed' ? 'bg-green-100 text-green-700' :
                        job.status?.includes('Quote') ? 'bg-amber-100 text-amber-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{job.status}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Contractors */}
            {results.contractors.length > 0 && (
              <div className="p-2 border-b border-slate-100">
                <p className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Contractors</p>
                {results.contractors.map((contractor) => {
                  const isSelected = selectedIndex === globalIndex;
                  const currentIndex = globalIndex++;
                  return (
                    <button
                      key={contractor.id}
                      data-index={currentIndex}
                      onClick={() => handleSelect(currentIndex)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                        isSelected ? 'bg-teal-50 ring-2 ring-teal-500/20' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isSelected ? 'bg-teal-100' : 'bg-purple-50'}`}>
                        <Users className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-purple-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>
                          {contractor.company_name || contractor.name}
                        </p>
                        <p className="text-xs text-slate-400">{contractor.primary_trade}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Parks */}
            {results.parks.length > 0 && (
              <div className="p-2 border-b border-slate-100">
                <p className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Parks</p>
                {results.parks.map((park) => {
                  const isSelected = selectedIndex === globalIndex;
                  const currentIndex = globalIndex++;
                  return (
                    <button
                      key={park.id || park.park_id}
                      data-index={currentIndex}
                      onClick={() => handleSelect(currentIndex)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                        isSelected ? 'bg-teal-50 ring-2 ring-teal-500/20' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isSelected ? 'bg-teal-100' : 'bg-emerald-50'}`}>
                        <Building2 className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-emerald-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>
                          {park.name}
                        </p>
                        <p className="text-xs text-slate-400">{park.postcode}</p>
                      </div>
                    </button>
                  );
                })}
              </div>
            )}

            {/* Invoices */}
            {results.invoices.length > 0 && (
              <div className="p-2 border-b border-slate-100">
                <p className="px-2 py-1 text-[10px] font-bold text-slate-400 uppercase tracking-wider">Invoices</p>
                {results.invoices.map((invoice) => {
                  const isSelected = selectedIndex === globalIndex;
                  const currentIndex = globalIndex++;
                  return (
                    <button
                      key={invoice.id}
                      data-index={currentIndex}
                      onClick={() => handleSelect(currentIndex)}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-left transition ${
                        isSelected ? 'bg-teal-50 ring-2 ring-teal-500/20' : 'hover:bg-slate-50'
                      }`}
                    >
                      <div className={`flex items-center justify-center w-8 h-8 rounded-lg ${isSelected ? 'bg-teal-100' : 'bg-amber-50'}`}>
                        <Receipt className={`w-4 h-4 ${isSelected ? 'text-teal-600' : 'text-amber-500'}`} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className={`text-sm font-medium truncate ${isSelected ? 'text-teal-700' : 'text-slate-700'}`}>
                          {invoice.invoice_number || `INV-${invoice.id?.slice(0,6)}`}
                        </p>
                        <p className="text-xs text-slate-400">£{invoice.amount?.toLocaleString() || '0'}</p>
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        invoice.status === 'paid' ? 'bg-green-100 text-green-700' :
                        invoice.status === 'overdue' ? 'bg-red-100 text-red-700' :
                        'bg-slate-100 text-slate-600'
                      }`}>{invoice.status}</span>
                    </button>
                  );
                })}
              </div>
            )}

            {/* No results */}
            {query && !loading && flatResults.length === 0 && (
              <div className="p-8 text-center">
                <Search className="w-10 h-10 mx-auto mb-3 text-slate-200" />
                <p className="text-sm font-medium text-slate-600">No results found for "{query}"</p>
                <p className="text-xs text-slate-400 mt-1">Try searching for jobs, contractors, or parks</p>
              </div>
            )}

            {/* Initial state hint */}
            {!query && (
              <div className="p-4 text-center border-t border-slate-100 bg-slate-50/50">
                <p className="text-xs text-slate-400">
                  <span className="font-medium">Pro tip:</span> Use this command bar for faster navigation. 
                  Type "new job", "overdue", or any park/contractor name.
                </p>
              </div>
            )}
          </div>

          {/* Footer - Keyboard hints */}
          <div className="px-4 py-2.5 border-t border-slate-100 bg-slate-50 flex items-center justify-between text-xs text-slate-400">
            <div className="flex items-center gap-4">
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  <kbd className="w-5 h-5 flex items-center justify-center rounded bg-white border border-slate-200 shadow-sm">
                    <ArrowUp className="w-3 h-3" />
                  </kbd>
                  <kbd className="w-5 h-5 flex items-center justify-center rounded bg-white border border-slate-200 shadow-sm">
                    <ArrowDown className="w-3 h-3" />
                  </kbd>
                </div>
                <span>navigate</span>
              </div>
              <div className="flex items-center gap-1.5">
                <kbd className="h-5 px-1.5 flex items-center justify-center rounded bg-white border border-slate-200 shadow-sm text-[10px] font-medium">
                  Enter
                </kbd>
                <span>select</span>
              </div>
            </div>
            <div className="flex items-center gap-1">
              <Command className="w-3 h-3" />
              <span>+</span>
              <kbd className="h-5 px-1.5 flex items-center justify-center rounded bg-white border border-slate-200 shadow-sm text-[10px] font-medium">K</kbd>
              <span className="ml-1">to open</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// =============================================================================
// HOOK - Command Bar state management with keyboard shortcut
// =============================================================================
export function useCommandBar() {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Cmd+K or Ctrl+K to toggle
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setIsOpen(prev => !prev);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, []);

  return { 
    isOpen, 
    setIsOpen, 
    open: () => setIsOpen(true), 
    close: () => setIsOpen(false),
    toggle: () => setIsOpen(prev => !prev)
  };
}
